/**
 * Franchise list view component (table-like)
 */
import { useTranslation } from 'react-i18next';
import { Franchise } from '@/types';

interface FranchiseListViewProps {
  franchises: Franchise[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
}

export function FranchiseListView({ franchises, onEdit, onDelete }: FranchiseListViewProps) {
  const { t } = useTranslation();
  const getPlanBadgeColor = (tier: string) => {
    switch (tier) {
      case 'free': return 'bg-surface-2 text-ink';
      case 'basic': return 'bg-info/10 text-info';
      case 'premium': return 'bg-accent/10 text-accent';
      case 'enterprise': return 'bg-warning/10 text-warning';
      default: return 'bg-surface-2 text-ink';
    }
  };

  return (
    <div className="bg-surface shadow-md rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-hairline">
          <thead className="bg-surface-2">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                {t('franchises.table.logo')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                {t('franchises.table.name')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                {t('franchises.table.contact')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                {t('franchises.table.plan')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-ink-subtle uppercase tracking-wider">
                {t('franchises.table.status')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-ink-subtle uppercase tracking-wider">
                {t('franchises.table.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-hairline">
            {franchises.map((franchise) => (
              <tr key={franchise.franchiseId} className="hover:bg-surface-2 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  {franchise.logo ? (
                    <img
                      src={franchise.logo}
                      alt={franchise.name}
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-surface-2 flex items-center justify-center text-2xl">
                      🏢
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-ink">{franchise.name}</div>
                  {franchise.description && (
                    <div className="text-sm text-ink-subtle truncate max-w-xs">
                      {franchise.description}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-ink">{franchise.email}</div>
                  <div className="text-sm text-ink-subtle">{franchise.phone}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPlanBadgeColor(franchise.planTier)}`}>
                    {t(`franchises.planTiers.${franchise.planTier}`)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    franchise.isActive
                      ? 'bg-success/10 text-success'
                      : 'bg-danger/10 text-danger'
                  }`}>
                    {franchise.isActive ? t('common.active') : t('common.inactive')}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => onEdit(franchise.franchiseId)}
                    className="text-info hover:text-info mr-4"
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    onClick={() => onDelete(franchise.franchiseId)}
                    className="text-danger hover:text-danger"
                  >
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
